import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import {
  AGGREGATION_TYPE,
  GRAPH_DATA_TYPE,
  GRAPH_PERIODICITY,
} from '../../../../../utils/enums/generic.enum';
import { GenerationStyles as styles } from './generation.styles';
import { GenerationTemplate as template } from './generation.template';
import Highcharts from 'highcharts';
import { IProjectForecastTs, IProjectHistoricalTs } from '../../../../../types';
import { getDatesFromData, getValuesFromData } from '../../../../../utils/chart-data-utils';

const name = 'generation-mwh';

@customElement({
  name,
  template,
  styles,
})
export class Generation extends FASTElement {
  @observable historicalData: Array<IProjectHistoricalTs>;
  @observable forecastData: Array<IProjectForecastTs>;
  contractCapacity: number;
  nameplate: number;

  @observable generationChart!: HTMLDivElement;
  @observable dataType: GRAPH_DATA_TYPE = GRAPH_DATA_TYPE.FORECAST;
  @observable periodicity: GRAPH_PERIODICITY = GRAPH_PERIODICITY.QUATERLY;

  @observable isVisible: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    this.renderGeneration();
  }

  public renderGraph(
    historicalData: Array<IProjectHistoricalTs>,
    forecastData: Array<IProjectForecastTs>,
    contractCapacity: number,
    nameplate: number
  ) {
    if (historicalData) {
      // Get a date object for the current time
      const minimumDate = new Date();

      // Set it to three years ago
      minimumDate.setFullYear(minimumDate.getFullYear() - 3);
      historicalData = historicalData.filter((x) => x.METRICS_DATETIME > minimumDate.getTime());
    }

    this.historicalData = historicalData ? historicalData.slice() : [];
    this.forecastData = forecastData ? forecastData.slice() : [];
    this.contractCapacity = contractCapacity;
    this.nameplate = nameplate;
    this.renderGeneration();
  }

  public dataTypeChanged(oldValue, newValue) {
    this.renderGeneration();
  }

  public periodicityChanged(oldValue, newValue) {
    this.renderGeneration();
  }

  public reflow() {
    this.isVisible = false;
    setTimeout(() => {
      this.renderGeneration();
    }, 1);
  }

  private renderGeneration() {
    if (
      this.dataType === GRAPH_DATA_TYPE.HISTORICAL &&
      (!this.historicalData || this.historicalData.length === 0)
    ) {
      this.isVisible = false;
      return;
    }

    if (
      this.dataType === GRAPH_DATA_TYPE.FORECAST &&
      (!this.forecastData || this.forecastData.length === 0)
    ) {
      this.isVisible = false;
      return;
    }

    this.isVisible = true;

    const dates = getDatesFromData(
      this.dataType === GRAPH_DATA_TYPE.FORECAST ? this.forecastData : this.historicalData,
      this.periodicity
    );

    const historical = getValuesFromData(
      this.historicalData,
      'GENERATION_MWH',
      this.periodicity,
      this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
    );

    const forecast = getValuesFromData(
      this.forecastData,
      'GENERATION_MEAN_MWH',
      this.periodicity,
      this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
    );

    const generationMWH = this.dataType === GRAPH_DATA_TYPE.HISTORICAL ? historical : forecast;

    // Project Specific Generation MWH
    const contractPercentage = this.contractCapacity / this.nameplate;
    let projectHistorical = historical.map((i) => i / contractPercentage);
    let projectForecast = forecast.map((i) => i / contractPercentage);

    const projectGenerationMWH =
        this.dataType === GRAPH_DATA_TYPE.HISTORICAL ? projectHistorical : projectForecast;

    // Net Capacity Factor Calculation
    const historicalHours = getValuesFromData(
        this.historicalData,
        'HOURS',
        this.periodicity,
        this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
    );

    const forecastHours = getValuesFromData(
        this.forecastData,
        'HOURS',
        this.periodicity,
        this.periodicity !== GRAPH_PERIODICITY.MONTHLY ? AGGREGATION_TYPE.SUM : null
    );

    let historicalGenerationPotential = historicalHours.map((i) => i * this.nameplate);
    let forecastGenerationPotential = forecastHours.map((i) => i * this.nameplate);

    // Function to calculate Net Capacity Factor as a percentage
    const calculateNetCapacityFactor = (actualGenerationArray: number[], potentialGenerationArray: number[]): number[] => {
      return actualGenerationArray.map((actualGeneration, index) => {
        const potentialGeneration = potentialGenerationArray[index];
        // Round value to 4 decimal places, convert back to a float and multiply by 100 to get the percentage
        return parseFloat((actualGeneration / potentialGeneration).toFixed(4)) * 100;
      });
    };

    // Calculate historical and forecast net capacity factors
    let netCapacityHistorical = calculateNetCapacityFactor(projectHistorical, historicalGenerationPotential);
    let netCapacityForecast = calculateNetCapacityFactor(projectForecast, forecastGenerationPotential);

    const netCapacityFactor =
      this.dataType === GRAPH_DATA_TYPE.HISTORICAL ? netCapacityHistorical : netCapacityForecast;

    Highcharts.chart({
      chart: {
        renderTo: this.generationChart,
      },
      title: {
        text: '',
      },
      xAxis: {
        categories: dates,
      },
      yAxis: [
        {
          // Primary yAxis
          title: {
            text: 'Generation (MWh)',
          },
          labels: {
            format: '{value:,.2f}',
          },
          tickAmount: 6,
        },
        {
          // Secondary yAxis
          title: {
            text: 'Net Capacity Factor (%)',
            rotation: -90,
            margin: 20,
          },
          labels: {
            format: '{value:,.2f}',
          },
          opposite: true,
          min: 0,
          max: 100,
          tickAmount: 6,
        },
      ],
      tooltip: {
        valueDecimals: 2,
      },
      series: [
        {
          type: 'column',
          color: Highcharts.getOptions().colors[2],
          yAxis: 0,
          name: this.getProjectAreaName(),
          data: projectGenerationMWH,
          zIndex: 1,
        },
        {
          type: 'column',
          color: Highcharts.getOptions().colors[1],
          yAxis: 0,
          name: this.getContractAreaName(),
          data: generationMWH,
          zIndex: 1,
        },
        {
          type: 'line',
          color: Highcharts.getOptions().colors[6],
          yAxis: 1,
          name: 'Net Capacity Factor (%)',
          data: netCapacityFactor,
          zIndex: 2,
          tooltip: {
            valueSuffix: '%',
          },
        },
      ],
    });
  }

  private getProjectAreaName(): string {
    return this.dataType === GRAPH_DATA_TYPE.FORECAST ? 'Project-Level Generation Forecast P50 (MWh)' : 'Project-Level Generation (MWh)';
  }

  private getContractAreaName(): string {
    return this.dataType === GRAPH_DATA_TYPE.FORECAST ? 'Contracted Generation Forecast P50 (MWh)' : 'Contracted Generation (MWh)';
  }
}
